
import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Producto, Rol } from '../types';
import Modal from '../components/Modal';

const ProductoForm: React.FC<{ producto: Partial<Producto>; onSave: (producto: Partial<Producto>) => Promise<void>; onCancel: () => void; }> = ({ producto, onSave, onCancel }) => {
    const [formData, setFormData] = useState<Partial<Producto>>({
        nombre: '',
        categoria: '',
        precio: 0,
        estado: 'activo',
        canjeable_por_puntos: false,
        puntos_para_canje: 0,
        ...producto
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        let newValue: any = value;
        if (type === 'number') newValue = parseFloat(value);
        else if (type === 'checkbox') newValue = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        await onSave(formData);
        setIsSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Nombre del Producto</label>
                <input type="text" name="nombre" value={formData.nombre || ''} onChange={handleChange} className="w-full p-2 border rounded mt-1" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Categoría</label>
                    <input type="text" name="categoria" value={formData.categoria || ''} onChange={handleChange} className="w-full p-2 border rounded mt-1" required placeholder="Ej: Bebidas" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Precio</label>
                    <input type="number" name="precio" value={formData.precio || ''} onChange={handleChange} className="w-full p-2 border rounded mt-1" required min="0" />
                </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
                <input type="checkbox" id="canjeable_por_puntos" name="canjeable_por_puntos" checked={formData.canjeable_por_puntos || false} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <label htmlFor="canjeable_por_puntos" className="text-sm font-medium text-gray-700">¿Es canjeable por puntos?</label>
            </div>
            {formData.canjeable_por_puntos && (
                <div>
                    <label className="block text-sm font-medium text-gray-700">Puntos necesarios para canje</label>
                    <input type="number" name="puntos_para_canje" value={formData.puntos_para_canje || ''} onChange={handleChange} className="w-full p-2 border rounded mt-1" min="1" required={formData.canjeable_por_puntos} />
                </div>
            )}
            <div>
                <label className="block text-sm font-medium text-gray-700">Estado</label>
                <select name="estado" value={formData.estado || 'activo'} onChange={handleChange} className="w-full p-2 border rounded mt-1">
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={onCancel} className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400">Cancelar</button>
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400" disabled={isSaving}>
                    {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </form>
    );
};

const ProductosScreen: React.FC = () => {
    const { user, productos, addProducto, updateProducto, deleteProducto, isReadOnly } = useContext(AppContext);
    const ui = useUI();
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProducto, setSelectedProducto] = useState<Producto | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [productoToDelete, setProductoToDelete] = useState<Producto | null>(null);

    const filteredProductos = useMemo(() =>
        productos.filter(p =>
            (p.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.categoria || '').toLowerCase().includes(searchTerm.toLowerCase())
        ), [productos, searchTerm]
    );
    
    const canEdit = user?.rol === Rol.ADMIN && !isReadOnly;

    const toggleExpand = (id: number) => setExpandedProductId(prevId => prevId === id ? null : id);

    const handleOpenModal = (producto?: Producto) => {
        setSelectedProducto(producto || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProducto(null);
    };

    const handleDeleteClick = (e: React.MouseEvent, producto: Producto) => {
        e.stopPropagation(); 
        if (deletingId) return;
        setProductoToDelete(producto);
    };

    const confirmDelete = async () => {
        if (!productoToDelete) return;
        const id = productoToDelete.id;
        
        setDeletingId(id);
        setProductoToDelete(null);

        try {
            const result = await deleteProducto(id);
            if (!result.success) {
                ui.showModal('Error', result.message || "Error desconocido al eliminar.");
            }
        } catch (error: any) {
            ui.showModal('Error', `Error inesperado: ${error.message}`);
        } finally {
            setDeletingId(null);
        }
    };

    const handleSaveProducto = async (productoData: Partial<Producto>) => {
        try {
            let success = false;
            if (selectedProducto) {
                success = await updateProducto({ ...selectedProducto, ...productoData } as Producto);
            } else {
                const newProducto = await addProducto(productoData as Omit<Producto, 'id' | 'club_id'>);
                success = !!newProducto;
            }
            if (success) handleCloseModal();
        } catch (error: any) {
            ui.showModal('Error', error.message || 'Error inesperado');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800 w-full sm:w-auto">Productos y Precios</h2>
                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                         <input
                            type="text"
                            placeholder="Buscar por nombre o categoría..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full sm:w-72 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {canEdit && (
                            <button 
                                onClick={() => handleOpenModal()} 
                                className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 font-semibold whitespace-nowrap disabled:bg-gray-400"
                            >
                                Nuevo Producto
                            </button>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="border-t border-gray-200">
                <div className="hidden md:block">
                    <div className="grid grid-cols-12 gap-4 bg-gray-50 px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="col-span-4">Nombre</div>
                        <div className="col-span-2">Categoría</div>
                        <div className="col-span-2">Precio</div>
                        <div className="col-span-1">Canjeable</div>
                        <div className="col-span-1">Estado</div>
                        {canEdit && <div className="col-span-2 text-right">Acciones</div>}
                    </div>

                    <div className="divide-y divide-gray-200">
                        {filteredProductos.map(producto => (
                            <div key={producto.id} className="px-6 py-4 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-4"><p className="text-sm font-medium text-gray-900">{producto.nombre}</p></div>
                                <div className="col-span-2"><p className="text-sm text-gray-600">{producto.categoria}</p></div>
                                <div className="col-span-2"><p className="text-sm text-gray-600">${producto.precio.toLocaleString('es-AR')}</p></div>
                                <div className="col-span-1"><p className="text-sm text-gray-600">{producto.canjeable_por_puntos ? 'Sí' : 'No'}</p></div>
                                <div className="col-span-1"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${producto.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{producto.estado}</span></div>
                                {canEdit && (
                                    <div className="col-span-2 text-right">
                                        <button onClick={() => handleOpenModal(producto)} className="text-yellow-600 hover:text-yellow-900 text-sm font-medium">Editar</button>
                                        <button 
                                            onClick={(e) => handleDeleteClick(e, producto)} 
                                            disabled={deletingId === producto.id}
                                            className="text-red-600 hover:text-red-900 text-sm font-medium ml-4 disabled:text-gray-400"
                                        >
                                            {deletingId === producto.id ? '...' : 'Eliminar'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Mobile View */}
                <div className="divide-y divide-gray-200 md:hidden">
                    {filteredProductos.map(producto => {
                        const isExpanded = expandedProductId === producto.id;
                        return (
                            <div key={producto.id} className="p-4">
                                <div className="flex justify-between items-center w-full">
                                    <div onClick={() => toggleExpand(producto.id)} className="flex-grow cursor-pointer">
                                        <h3 className="text-lg font-semibold text-gray-800">{producto.nombre}</h3>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {canEdit && (
                                            <>
                                                <button onClick={() => handleOpenModal(producto)} className="text-yellow-600 hover:text-yellow-900 text-sm font-medium px-2 py-1">Editar</button>
                                                <button 
                                                    onClick={(e) => handleDeleteClick(e, producto)} 
                                                    disabled={deletingId === producto.id}
                                                    className="text-red-600 hover:text-red-900 text-sm font-medium px-2 py-1 disabled:text-gray-400"
                                                >
                                                    {deletingId === producto.id ? '...' : 'Eliminar'}
                                                </button>
                                            </>
                                        )}
                                        <div onClick={() => toggleExpand(producto.id)} className="cursor-pointer p-2">
                                            <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                        </div>
                                    </div>
                                </div>
                                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                                    <div className="space-y-3 pt-2 border-t mt-2">
                                        <div className="flex justify-between"><span className="text-gray-500">Categoría</span><span className="font-medium text-gray-800">{producto.categoria}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Precio</span><span className="font-medium text-gray-800">${producto.precio.toLocaleString('es-AR')}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Estado</span><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${producto.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{producto.estado}</span></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {isModalOpen && (
                <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={selectedProducto ? 'Editar Producto' : 'Nuevo Producto'}>
                    <ProductoForm producto={selectedProducto || {}} onSave={handleSaveProducto} onCancel={handleCloseModal} />
                </Modal>
            )}

            {/* Confirmation Modal */}
            {productoToDelete && (
                <Modal isOpen={!!productoToDelete} onClose={() => setProductoToDelete(null)} title="Confirmar Inactivación">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            ¿Estás seguro de que querés inactivar el producto <span className="font-bold">{productoToDelete.nombre}</span>?
                        </p>
                        <p className="text-sm text-red-600 font-medium">Esta acción inactivará el producto en el catálogo, pero no afectará a las ventas históricas.</p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button 
                                onClick={() => setProductoToDelete(null)} 
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmDelete} 
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                            >
                                Inactivar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ProductosScreen;
